"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.mergeOldMpiShapefile = exports.mergeEligibleMpiShapefile = exports.bestGuessStatus = void 0;
const turf_1 = require("@turf/turf");
const lodash_1 = __importDefault(require("lodash"));
const geojson_converters_1 = require("./geojson-converters");
const mpi_type_converters_1 = require("./mpi-type-converters");
const bbox_overlap_1 = require("./operations/bbox-overlap");
const remove_small_1 = require("./operations/remove-small");
/**
 * Determines if a credit scheme is NZETS
 *
 * @param scheme credit scheme
 * @returns true if credit scheme is run by NZ ETS, false otherwise
 */
const isEtsScheme = (scheme) => ['ets', 'ets_stock_change', 'ets_averaging'].includes(scheme !== null && scheme !== void 0 ? scheme : 'ets');
/**
 * Attempts to figure out what status a polygon is in
 *
 * @param props
 */
const bestGuessStatus = (props) => {
    if (props.status === 'rejected' || props.status === 'registered') {
        return props.status;
    }
    if (props.status === 'submitted') {
        console.warn('Please review bestGuessStatus() - it appears submitted status is now a reality');
        return props.status;
    }
    // It is now in an implicitly defined state, so we should make some guesses about if it is draft or submitted
    const submittableForests = [
        'existing_exotic_ets_forest',
        'existing_indigenous_ets_forest',
    ];
    if (!submittableForests.includes(props.report_category)) {
        // It won't have been submitted if it isn't a submittable forest type
        return 'draft';
    }
    if (!props.ets_caa_number) {
        // It won't have been submitted if it doesn't have a CAA number
        return 'draft';
    }
    return 'submitted';
};
exports.bestGuessStatus = bestGuessStatus;
/**
 * Converts from a year number (eg "2022") into an iso
 * date (eg "2022-01-01").
 * Technically a 4 digit year is ISO compliant, but some libraries
 * (python, cough, python) don't support it.
 *
 * @param year 4-digit year
 * @returns yyy-mm-dd
 */
const yearToISODate = (year) => `${year}-01-01`;
/**
 * Combine the properties of an MPI shapefile and an ETS segment
 *
 * All the properties of the segment are preserved, but some
 * may be overwritten by ones in the MPI properties.
 * The ones that can be overwritten include:
 *  - The established (or previously established) date,
 *  - The CAA number
 *  - the ETS species
 *
 * @param segmentsProperties properties of one of our frontend segments
 * @param mpiProperties properties from an old MPI shapefile
 * @returns The properties for a frontend segment
 */
const mergeOldProperties = (segmentsProperties, mpiProperties) => {
    const speciesData = mpi_type_converters_1.MPI_TO_ETS_SPECIES[mpiProperties.SPECIES];
    if (!speciesData) {
        throw new Error(`Unknown Species in MPI Shapefile: ${mpiProperties.SPECIES}`);
    }
    return {
        ...segmentsProperties,
        ets_caa_number: mpiProperties.CAA_NUM,
        ets_species: speciesData !== null && speciesData !== void 0 ? speciesData : segmentsProperties.ets_species,
        credit_scheme: segmentsProperties.credit_scheme || 'ets',
        // MPI's YEAR_PLANT field describes when it was first planted, so we have to select which field to write into:
        established_date: !segmentsProperties.previous_established_date
            ? yearToISODate(mpiProperties.YEAR_PLANT)
            : segmentsProperties.established_date,
        previous_established_date: !segmentsProperties.previous_established_date
            ? segmentsProperties.previous_established_date
            : yearToISODate(mpiProperties.YEAR_PLANT),
        report_category: mpi_type_converters_1.ETS_SPECIES_TO_REPORT_CATEGORY[speciesData],
        ets_registration: lodash_1.default.pick(mpiProperties, ['FOREST_NUM', 'STAND_NUM', 'COMP_NUM']),
    };
};
/**
 * Combine the properties of an MPI shapefile and an ETS segment
 *
 * All the properties of the segment are preserved, but some
 * may be overwritten by ones in the MPI properties.
 * The ones that can be overwritten include:
 *  - The established (or previously established) date,
 *  - The CAA number
 *  - the ETS species
 *
 * @param segmentsProperties properties of one of our frontend segments
 * @param mpiProperties properties from an eligible MPI shapefile
 * @returns The properties for a frontend segment
 */
const mergeEligibleProperties = (segmentsProperties, mpiProperties) => {
    var _a;
    const speciesData = mpi_type_converters_1.MPI_FOREST_ID_TO_ETS_SPECIES[mpiProperties.FOREST_TYP];
    if (!speciesData) {
        throw new Error(`Unknown Species in MPI Shapefile: ${mpi_type_converters_1.MPI_FOREST_ID_TO_ETS_SPECIES[mpiProperties.FOREST_TYP]}`);
    }
    return {
        ...segmentsProperties,
        ets_caa_number: (_a = mpiProperties.CAA_NUMBER) !== null && _a !== void 0 ? _a : 0,
        ets_species: speciesData !== null && speciesData !== void 0 ? speciesData : segmentsProperties.ets_species,
        credit_scheme: segmentsProperties.credit_scheme || 'ets',
        // MPI's YEAR_PLANT field describes when it was first planted, so we have to select which field to write into:
        established_date: !segmentsProperties.previous_established_date
            ? yearToISODate(mpiProperties.YEAR_FIRST)
            : segmentsProperties.established_date,
        previous_established_date: !segmentsProperties.previous_established_date
            ? segmentsProperties.previous_established_date
            : yearToISODate(mpiProperties.YEAR_FIRST),
        report_category: mpi_type_converters_1.ETS_SPECIES_TO_REPORT_CATEGORY[speciesData],
        ets_registration: {
            FOREST_NUM: parseInt(mpiProperties.FOREST_NUM, 10),
            STAND_NUM: 0,
            COMP_NUM: 0,
        },
    };
};
/**
 * Returns True if the two features overlap _by a significant amount_
 * This is useful to deal with floating point precision issues which
 * mean that some features may accidentally touch/overlap
 *
 * @param featureA A geojson feature
 * @param featureB A geojson feature
 * @returns true if any part of them overlap
 */
const featureOverlapsWith = (featureA, featureB) => {
    // shrink each feature by a small amount:
    if (featureA.geometry.coordinates.length === 0 || featureB.geometry.coordinates.length === 0) {
        return false;
    }
    if (!(0, bbox_overlap_1.bboxOverlap)((0, turf_1.bbox)(featureA), (0, turf_1.bbox)(featureB))) {
        return false;
    }
    // shrink each feature by a small amount:
    // This overlap was determined experimentally. Smaller distances sometimes
    // resulted in incorrect overlap detection, and it is small enough that
    // compared to MPI's 15-m width requirements, it's unlikely to have any
    // issues with overly distorting the geometry
    const OVERLAP_THRESHOLD_METERS = 1;
    const smallFeatureA = (0, turf_1.buffer)(featureA, -OVERLAP_THRESHOLD_METERS, {
        units: 'meters',
    });
    const smallFeatureB = (0, turf_1.buffer)(featureB, -OVERLAP_THRESHOLD_METERS, {
        units: 'meters',
    });
    if (smallFeatureA && smallFeatureB) {
        return !(0, turf_1.booleanDisjoint)(smallFeatureA, smallFeatureB);
    }
    return false;
};
/**
 * Splits an array into two based on a condition.
 *
 * @param arr Array of items to iterate through
 * @param condition How to decide which array it goes into
 * @returns Two lists, the first one is for whenever the condition returns True, the second is for whenever it returns False
 */
const splitOnCondition = (arr, condition) => arr.reduce((accumulator, item, index) => {
    if (condition(item, index)) {
        return [[...accumulator[0], item], accumulator[1]];
    }
    return [accumulator[0], [...accumulator[1], item]];
}, [[], []]);
/**
 * Removes any polygons from a feature collection which are invalid (have less than 4 points)
 *
 * @param feat a feature collection of polygons
 * @param feature
 * @returns a new feature collection with invalid polygons removed
 */
const filterInvalidRings = (feature) => {
    const polygonCoordinateFilter = (coord) => {
        if (coord[0].length < 4)
            return [];
        return coord.filter((p) => p.length >= 4);
    };
    if (!feature)
        return null;
    if (feature.geometry.type === 'MultiPolygon') {
        const newCoord1 = feature.geometry.coordinates
            .map(polygonCoordinateFilter)
            .filter((p) => p.length > 0);
        return newCoord1.length === 0
            ? null
            : {
                ...feature,
                geometry: {
                    ...feature.geometry,
                    coordinates: newCoord1,
                },
            };
    }
    const newCoord2 = polygonCoordinateFilter(feature.geometry.coordinates);
    return newCoord2.length === 0
        ? null
        : {
            ...feature,
            geometry: {
                ...feature.geometry,
                coordinates: newCoord2,
            },
        };
};
const cropByReservedFeature = (bypassFeatures, reservedFeature) => {
    const [overlappingFeatures, nonOverlappingFeatures] = splitOnCondition(bypassFeatures, (bypassFeature) => featureOverlapsWith(bypassFeature, reservedFeature));
    const coppedFeatures = overlappingFeatures
        .map((feature) => {
        // The same buffer as others (0.001mm)
        const diff = (0, turf_1.difference)((0, turf_1.buffer)(feature, 1e-3, { units: 'millimeters' }), reservedFeature);
        return filterInvalidRings(diff);
    })
        .filter((f) => f !== null);
    return [...nonOverlappingFeatures, ...coppedFeatures];
};
const mergeMpiShapefile = (shapefileData, segments, mergePropertiesFn) => {
    // Features that aren't ETS credit scheme, or are already rejected can be ignored
    const [etsFeatures, bypassFeatures] = splitOnCondition(segments.features, (feature) => isEtsScheme(feature.properties.credit_scheme) &&
        ['submitted', 'registered'].includes((0, exports.bestGuessStatus)(feature.properties)));
    const combinedMpiAreas = shapefileData.features.length
        ? (0, turf_1.buffer)((0, geojson_converters_1.flattenFeatureCollection)(shapefileData), 0)
        : (0, turf_1.polygon)([]);
    const [overlappingFeatures, nonOverlappingFeatures] = splitOnCondition(etsFeatures, (etsFeature) => featureOverlapsWith(etsFeature, combinedMpiAreas));
    // If an area does not overlap:
    // - if it is in draft status we keep it there
    const preservedNonOverlappingFeatures = nonOverlappingFeatures.map((feature) => feature);
    // Find the bits of the segments that don't overlap and mark them as rejected
    const rejectedPartialFeatures1 = overlappingFeatures.flatMap((overlapFeature) => {
        // We buffer the polygon outwards by 0.001mm. This gets around the fact that MPI's
        // polygons went through two coordiate transforms, and accumulated double precision
        // errors along the way.
        //
        // 0.001mm is actually overkill. Emperical testing showed that 8 nanometers was big
        // enough to fix the issue. And this corresponds almost exactly to the double precision
        // loss, applied twice to a lat/lon of 6.4 nanometers.
        const diff = (0, turf_1.difference)((0, turf_1.buffer)(overlapFeature, 1e-3, { units: 'millimeters' }), combinedMpiAreas);
        const filteredDiff = filterInvalidRings(diff);
        if (filteredDiff !== null) {
            return [
                {
                    ...filteredDiff,
                    properties: {
                        ...overlapFeature.properties,
                        status: 'rejected',
                        ets_caa_number: undefined,
                    },
                },
            ];
        }
        // Full overlap, so there are no partial areas
        return [];
    });
    const rejectedPartialFeatures2 = (0, remove_small_1.removeSmallPolygons)((0, turf_1.featureCollection)(rejectedPartialFeatures1), 1).features;
    const usedIds = new Set();
    const acceptedAreas = shapefileData.features.map((mpiFeature) => {
        const overlappingSegment = etsFeatures.find((etsFeature) => featureOverlapsWith(etsFeature, mpiFeature));
        if (overlappingSegment) {
            // We found (at least) one overlapping segment
            const needBlankId = overlappingSegment.id && usedIds.has(overlappingSegment.id);
            const id = needBlankId ? undefined : overlappingSegment.id;
            if (!needBlankId) {
                usedIds.add(overlappingSegment.id);
            }
            return {
                ...overlappingSegment,
                geometry: mpiFeature.geometry,
                properties: {
                    ...mergePropertiesFn(overlappingSegment.properties, mpiFeature.properties),
                    status: 'registered',
                },
                id: id,
            };
        }
        // CAA that doesn't overlap any of our initial segments. We're probably importing to
        // a blank project
        return {
            ...mpiFeature,
            properties: {
                ...mergePropertiesFn({}, mpiFeature.properties),
                status: 'registered',
            },
        };
    });
    // Crop bypass features by the combined MPI areas
    const croppedBypassFeatures = cropByReservedFeature(bypassFeatures, combinedMpiAreas).filter((f) => f !== null);
    const finalBypassFeatures = (0, remove_small_1.removeSmallPolygons)((0, turf_1.featureCollection)(croppedBypassFeatures), 1).features;
    const outp = (0, turf_1.featureCollection)([
        ...rejectedPartialFeatures2,
        ...preservedNonOverlappingFeatures,
        ...acceptedAreas,
        ...finalBypassFeatures,
    ]);
    // Check for duplicate feature ID's which indicates something has gone wrong
    // (eg TWO CAA's mapped to the same base feature)
    const featureIds = outp.features.flatMap((f) => (f.id !== undefined ? [f.id] : []));
    const outFeatureIds = new Set(featureIds);
    if (outFeatureIds.size !== featureIds.length) {
        throw new Error("About to output duplicate ID's");
    }
    return outp;
};
const mergeEligibleMpiShapefile = (shapefileData, segments) => mergeMpiShapefile(shapefileData, segments, mergeEligibleProperties);
exports.mergeEligibleMpiShapefile = mergeEligibleMpiShapefile;
const mergeOldMpiShapefile = (shapefileData, segments) => mergeMpiShapefile(shapefileData, segments, mergeOldProperties);
exports.mergeOldMpiShapefile = mergeOldMpiShapefile;
